declare const __BUILD_ID__: string;

const RELOAD_GUARD_KEY = 'agnivo:update-reload';

/**
 * Pure reload decision — the testable core of the stale-bundle guard.
 *
 * Reload IFF the deployed build differs from the running one AND we haven't
 * already reloaded toward that same deployed build this session. Anything
 * uncertain (empty/unknown deployed id) → do NOT reload: the guard must never
 * spin or yank the app on bad data.
 */
export function shouldReloadForUpdate(args: {
  deployed: string | null | undefined;
  running: string;
  lastReloadedTo: string | null;
}): boolean {
  const { deployed, running, lastReloadedTo } = args;
  if (!deployed) return false; // no/empty deployed id → uncertain, never reload
  if (deployed === running) return false; // up to date
  if (deployed === lastReloadedTo) return false; // loop guard: already tried this target
  return true;
}

/**
 * Stale-bundle self-heal for the Telegram WebView.
 *
 * Telegram's in-app WebView caches the launch document aggressively — on
 * 2026-07-13 a redeploy did not reach Dmitry's client despite index.html being
 * served `no-cache, must-revalidate`; only a full Telegram app-reset picked up
 * the new bundle. Content-hashing + no-cache headers are necessary but not
 * sufficient inside Telegram. So the running app checks, on launch, whether it
 * is the currently-deployed build and force-reloads onto the fresh one if not.
 *
 * Fail-safe by construction: version.json is fetched cache-busted + no-store (so
 * it's fresh even if the WebView ignores no-cache), and ANY fetch/parse error
 * → no reload. The reload targets a cache-busted entry URL (`?v=<id>`) so the
 * WebView treats it as a new URL and re-fetches index.html (→ new hashed
 * assets). The memory router ignores the query and initData comes from the
 * Telegram SDK object, so the `?v=` is routing- and auth-safe.
 *
 * Called fire-and-forget AFTER first paint (main.tsx) so an up-to-date launch —
 * the overwhelming common case — pays only one cheap background fetch and never
 * blocks LCP; only a genuinely stale launch briefly renders before reloading.
 */
export async function guardAgainstStaleBundle(): Promise<void> {
  let deployed: string | null = null;
  try {
    const res = await fetch(`./version.json?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const body: unknown = await res.json();
    const id = (body as { buildId?: unknown } | null)?.buildId;
    deployed = typeof id === 'string' && id ? id : null;
  } catch {
    return; // offline / not served / non-JSON — never reload on uncertainty
  }

  const lastReloadedTo = safeSessionGet(RELOAD_GUARD_KEY);
  if (!shouldReloadForUpdate({ deployed, running: __BUILD_ID__, lastReloadedTo })) return;

  // Record the target BEFORE reloading so a reload that fails to land the new
  // bundle doesn't spin (we won't reload toward the same id twice per session).
  safeSessionSet(RELOAD_GUARD_KEY, deployed as string);

  const url = new URL(window.location.href);
  url.searchParams.set('v', deployed as string);
  window.location.replace(url.toString());
}

// sessionStorage can throw (private mode / disabled). Treat any failure as
// "no record" / best-effort write — the loop guard degrades, it never breaks.
function safeSessionGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* best-effort */
  }
}
